import moment from "moment";
import { writable } from "svelte/store";

import FilterHelper from "../Helpers/FilterHelper";
import FilterPropertyType from "./FilterPropertyType";
import FilterOperator from "./FilterOperator";

class FilterClause {
	constructor(set) {
		this.property = "";
		this.operator = "";
		this.value = "";
	}
}

class FilterSet {
	constructor(filters) {
		this.parent = filters;
		this.clauses = [];
	}

	addClause() {
		const clause = new FilterClause(this);
		this.clauses = [...this.clauses, clause];
		console.log("clauses", this.clauses);
		this.parent.onChange();
		return clause;
	}

	removeClause(clause) {
		const ind = this.clauses.indexOf(clause);
		if (ind >= 0) {
			this.clauses.splice(ind, 1);
		}
		this.parent.onChange();
	}

	addOrRemoveClauseIfNecessary() {
		if (this.clauses.length === 0 || this.clauses[this.clauses.length - 1].property !== "") {
			this.addClause();
		}
		for (let i = this.clauses.length - 1; i >= 0; i--) {
			if (this.clauses[i].property === "" && i !== this.clauses.length - 1) {
				this.removeClause(this.clauses[i]);
				i = this.clauses.length;
			}
		}
		this.parent.onChange();
	}
}

class Filters {
	constructor(properties) {
		this.properties = properties;
		this.propertiesKeyed = {};
		for (let i = 0; i < this.properties.length; i++) {
			const property = this.properties[i];
			this.propertiesKeyed[property.key] = property;
		}

		// If this is true, the items passed to apply() must have magic getters for their properties.
		// If it is false, the items will be treated as GeoJSON Features and thus will have their properties on `feature.properties`.
		this.usingMagicGetters = true;

		this.multisetEnabled = true;

		this.sets = [];
		this.lastUpdated = writable(0);
		this.onChange = () => {
			this.lastUpdated.set(Date.now());
		};
	}

	get length() {
		return this.sets.length;
	}

	setPropertyEnumOptions(key, options) {
		this.propertiesKeyed[key].typeData.options = options;
	}

	addSet() {
		if (!this.multisetEnabled && this.sets.length === 1) {
			return;
		}

		const set = new FilterSet(this);
		set.addClause();
		this.sets = [...this.sets, set];
		return set;
	}

	duplicateSet(set) {
		if (!this.multisetEnabled && this.sets.length === 1) {
			return;
		}

		const dupeClauses = JSON.parse(JSON.stringify(set.clauses));
		const newSet = this.addSet();
		newSet.clauses = dupeClauses;
		this.onChange();
	}

	removeSet(set) {
		const ind = this.sets.indexOf(set);
		if (ind >= 0) {
			this.sets.splice(ind, 1);
		}
		this.onChange();
	}

	reset() {
		this.sets = [];
		this.onChange();
	}

	apply(data) {
		const original = data;
		let filtered = [];
		if (this.sets.length === 0) {
			filtered = original;
		}
		else {
			for (let i = 0; i < this.sets.length; i++) {
				const set = this.sets[i];
				for (let j = 0; j < original.length; j++) {
					const item = original[j];
					const itemProperties = (this.usingMagicGetters) ? item : item.properties;
					let match = true;
					for (let k = 0; k < set.clauses.length; k++) {
						const condition = set.clauses[k];
						if (condition.operator === FilterOperator.IS ||
							condition.operator === FilterOperator.IS_NOT ||
							condition.operator === FilterOperator.LIKE ||
							condition.operator === FilterOperator.NOT_LIKE) {
							let sourceVal = itemProperties[condition.property];
							let compareVal = condition.value;
							const propData = this.propertiesKeyed[condition.property];
							if (propData.type === FilterPropertyType.STRING) {
								sourceVal = "" + itemProperties[condition.property];
								if (typeof propData.typeData !== "undefined") {
									if (typeof propData.typeData.caseSensitive !== "undefined") {
										if (!propData.typeData.caseSensitive) {
											sourceVal = sourceVal.toLowerCase();
											compareVal = compareVal.toLowerCase();
										}
									}
								}
							}
							if (condition.operator === FilterOperator.IS) {
								if (sourceVal !== compareVal) {
									match = false;
									break;
								}
							}
							else if (condition.operator === FilterOperator.IS_NOT) {
								if (sourceVal === compareVal) {
									match = false;
									break;
								}
							}
							else if (condition.operator === FilterOperator.LIKE) {
								if (sourceVal.indexOf(compareVal) === -1) {
									match = false;
									break;
								}
							}
							else if (condition.operator === FilterOperator.NOT_LIKE) {
								if (sourceVal.indexOf(compareVal) !== -1) {
									match = false;
									break;
								}
							}
						}
						else if (condition.operator === FilterOperator.GT ||
								 condition.operator === FilterOperator.LT) {
							const propData = this.propertiesKeyed[condition.property];
							const propType = propData.type;

							let sourceNumber;
							let compareNumber;
							if (propType === FilterPropertyType.NUMBER) {
								sourceNumber = parseFloat(itemProperties[condition.property]);
								compareNumber = parseFloat(condition.value);
								if (Number.isNaN(sourceNumber)) {
									match = false;
									break;
								}
							}
							else if (propType === FilterPropertyType.RELATIVE_TIME) {
								const date = itemProperties[condition.property];
								sourceNumber = moment.utc(date);
								compareNumber = moment.utc().add(parseInt(condition.value), "hours");
							}
							if (condition.operator === FilterOperator.GT) {
								if (sourceNumber <= compareNumber) {
									match = false;
									break;
								}
							}
							else if (condition.operator === FilterOperator.LT) {
								if (sourceNumber >= compareNumber) {
									match = false;
									break;
								}
							}
						}
					}
					if (match) {
						filtered.push(item);
					}
				}
			}
		}
		filtered = filtered.filter(FilterHelper.unique);
		// console.log("original", original);
		// console.log("filtered", filtered);
		return filtered;
	}
}
export default Filters;
